import { useAccountType } from '@/hooks/useAccountType'
import { timeCategoryApi } from '@/lib/api/time-category.api'
import { useOrganizationStore } from '@/store/organization.store'
import { useQuery } from '@tanstack/react-query'

import type { TimeCategory } from '@scrambl/shared'

import { queryKeys } from '../query-keys'

/**
 * The organization's time categories, in their saved order. Active only by
 * default; settings pass `includeInactive` to manage archived ones too.
 *
 * Business-only: personal accounts and external members never load them, so
 * every picker built on this hook renders nothing for them.
 */
export function useTimeCategoriesQuery(options?: {
  includeInactive?: boolean
  enabled?: boolean
}) {
  const includeInactive = options?.includeInactive === true
  const organizationId = useOrganizationStore(
    state => state.currentOrganization?.id,
  )
  const isExternalUser = useOrganizationStore(
    state => state.currentOrganization?.isExternalAccess === true,
  )
  const { isPersonal } = useAccountType()

  return useQuery<TimeCategory[]>({
    queryKey: queryKeys.timeCategories.list(organizationId, includeInactive),
    queryFn: async () => {
      const categories = await timeCategoryApi.getAll({ includeInactive })
      return sortTimeCategories(categories)
    },
    enabled:
      options?.enabled !== false &&
      !!organizationId &&
      !isExternalUser &&
      !isPersonal,
    staleTime: 5 * 60 * 1000,
  })
}

/** Saved order first, then name, so lists never jump between renders. */
export function sortTimeCategories(categories: TimeCategory[]): TimeCategory[] {
  return [...categories].sort(
    (left, right) =>
      (left.order ?? 0) - (right.order ?? 0) ||
      left.name.localeCompare(right.name),
  )
}
